import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Banknote, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useLivePayments, useApprovePayout, type LivePayout } from "@/hooks/admin/useLivePayments";
import { LoadingState, ErrorState, EmptyState } from "@/components/dashboard/common/PageShell";

const requestStyle: Record<string, string> = {
  pending: "bg-amber-600",
  approved: "bg-blue-600",
  paid: "bg-emerald-600",
  rejected: "bg-red-600",
  cancelled: "bg-muted",
};

const paystackStyle = (s: string | null) => {
  if (!s) return "bg-muted text-foreground";
  if (s === "success") return "bg-emerald-600 text-white";
  if (["pending", "otp", "processing", "queued"].includes(s)) return "bg-amber-600 text-white";
  return "bg-red-600 text-white";
};

const LivePayments = () => {
  const { data, isLoading, error, refetch, isFetching } = useLivePayments();
  const approve = useApprovePayout();
  const payouts: LivePayout[] = data?.payouts ?? [];
  const zar = data?.balance?.find((b) => b.currency === "ZAR");

  const pendingTotal = payouts.filter((p) => p.status === "pending").reduce((s, p) => s + Number(p.amount), 0);
  const paidTotal = payouts.filter((p) => p.status === "paid").reduce((s, p) => s + Number(p.amount), 0);

  const onApprove = (p: LivePayout) =>
    approve.mutate(p.id, {
      onSuccess: (res) =>
        toast.success(
          res?.status === "success" ? "Transfer sent to the bank" : `Transfer ${res?.status ?? "queued"} at Paystack`,
        ),
      onError: (e) => toast.error(e instanceof Error ? e.message : "Transfer failed"),
    });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Live Payments</h2>
          <p className="text-sm text-muted-foreground">
            Every payout request with its live Paystack transfer status. Approving sends a real ZAR bank transfer.
          </p>
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { label: "Paystack balance", value: zar ? `R${(zar.balance / 100).toFixed(2)}` : "—" },
          { label: "Awaiting approval", value: `R${pendingTotal.toFixed(0)}` },
          { label: "Paid out", value: `R${paidTotal.toFixed(0)}` },
        ].map((s) => (
          <Card key={s.label}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">{s.label}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-foreground">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {isLoading ? (
        <LoadingState label="Loading live payments…" />
      ) : error ? (
        <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
      ) : payouts.length === 0 ? (
        <EmptyState icon={Banknote} title="No payout requests" description="Withdrawal requests appear here in real time." />
      ) : (
        <div className="space-y-3">
          {payouts.map((p) => (
            <Card key={p.id}>
              <CardContent className="p-4 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-foreground">R{Number(p.amount).toFixed(2)}</p>
                    <Badge className={`${requestStyle[p.status] ?? "bg-muted"} border-0 capitalize text-white`}>
                      {p.status}
                    </Badge>
                    <Badge className={`${paystackStyle(p.paystack_status)} border-0 capitalize`}>
                      Paystack: {p.paystack_status ?? "not sent"}
                    </Badge>
                  </div>
                  <p className="text-sm text-foreground mt-1">
                    {p.requester_name}
                    {p.requester_type ? ` · ${p.requester_type}` : ""}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {p.bank_name || "Bank"} · {p.account_holder || "—"} · ••••{p.account_number_last4} ·{" "}
                    {format(new Date(p.created_at), "d MMM yyyy HH:mm")}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 font-mono break-all">Ref {p.reference}</p>
                  {p.paystack_error && <p className="text-xs text-red-500 mt-1">{p.paystack_error}</p>}
                </div>

                {["pending", "approved"].includes(p.status) && p.paystack_status !== "success" && (
                  <Button className="gap-2" disabled={approve.isPending} onClick={() => onApprove(p)}>
                    {approve.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Approve &amp; pay
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default LivePayments;
